"use client"

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import {
  MagnifyingGlass,
  FunnelSimple,
  X,
  ArrowClockwise,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  Users,
  Phone,
  EnvelopeSimple,
  Percent,
  ListBullets,
  Tag,
  CaretDown,
  DotsThreeVertical,
  PencilSimple,
  TrashSimple,
} from "phosphor-react"

import { DateRangePicker, SearchableSelect, DeleteModal } from "../../components"
import ProviderModal from "./ProviderModal"
import ProviderDetailsModal from "./ProviderDetailsModal"
import providersService from "../../services/providersService"

/* ───────────────────────── dropdown positioning helpers ───────────────────────── */

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max)
}

function calcMenuPos(rect, menuW, menuH, gap = 8, pad = 8) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  // align right edges (like your current absolute right-0)
  let left = rect.right - menuW
  left = clamp(left, pad, vw - menuW - pad)

  const belowTop = rect.bottom + gap
  const aboveTop = rect.top - gap - menuH

  let top = belowTop
  if (belowTop + menuH > vh - pad) {
    if (aboveTop >= pad) top = aboveTop
    else top = clamp(belowTop, pad, vh - menuH - pad)
  }

  return { top, left }
}

export function getPlatformMeta(platform, className = "w-4 h-4") {
  const p = String(platform || "").toLowerCase()

  switch (p) {
    case "facebook":
    case "meta":
      return {
        name: "Meta",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            className={className}
            aria-hidden="true"
          >
            <path
              fill="#0081fb"
              d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
              c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28
              c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51
              c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40
              c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35
              c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57
              c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5
              c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
            />
            <linearGradient
              id="meta_g1"
              x1="42.304"
              x2="13.533"
              y1="24.75"
              y2="24.75"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0081fb" />
              <stop offset=".995" stopColor="#0064e1" />
            </linearGradient>
            <path
              fill="url(#meta_g1)"
              d="M4.918,15.456
              C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967
              c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362
              c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333
              c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754
              c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
            />
          </svg>
        ),
      }

    case "google":
      return {
        name: "Google",
        icon: (
          <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        ),
      }

    case "tiktok":
      return {
        name: "TikTok",
        icon: (
          <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
          </svg>
        ),
      }

    case "snapchat":
      return {
        name: "Snapchat", icon:
          (
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
              <path
                fill="#ffeb3b"
                d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"
              ></path>
              <path
                fill="#fff"
                d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.440,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267c-0.142,0-0.319-0.039-0.526-0.084 c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705 C28.045,34.91,26.451,36,24,36z"
              ></path>
            </svg>
          )
      }

    default:
      return { name: platform || "Unknown", icon: <Tag size={16} /> }
  }
}

/* ───────────────────────── main component ───────────────────────── */

export default function ProviderManagement() {
  // Data
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(false)

  // Details modal
  const [showDetails, setShowDetails] = useState(false)
  const [detailsId, setDetailsId] = useState(null)

  const openDetails = useCallback((p) => {
    setDetailsId(p?._id || null)
    setShowDetails(true)
  }, [])

  const closeDetails = useCallback(() => {
    setShowDetails(false)
    setDetailsId(null)
  }, [])

  // Draft filters (UI)
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({ platform: "" })

  // Applied query state (drives fetching)
  const [queryState, setQueryState] = useState({
    platform: "",
    searchValue: "",
    dateRange: { start: "", end: "" },
    pagination: { currentPage: 1, pageSize: 5, totalPages: 1, totalItems: 0 },
    refreshKey: 0,
  })

  const pagination = queryState.pagination

  // Modals
  const [showProviderModal, setShowProviderModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  // Prevent stale requests overriding new data
  const reqIdRef = useRef(0)

  /* ───────────────────── Page Title ───────────────────── */
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Provider Management" },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [])

  /* ───────────────────── Derived ───────────────────── */
  const filtersActive = useMemo(() => {
    return (
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.platform !== ""
    )
  }, [searchQuery, dateRange, filters])

  /* ───────────────────── Fetch Providers ───────────────────── */
  const fetchProviders = useCallback(async () => {
    const reqId = ++reqIdRef.current
    try {
      setLoading(true)

      const payload = {
        platform: queryState.platform,
        searchValue: queryState.searchValue,
        dateRange: queryState.dateRange,
        pagination: {
          page: queryState.pagination.currentPage,
          limit: queryState.pagination.pageSize,
        },
      }

      const response = await providersService.search(payload)
      if (reqId !== reqIdRef.current) return

      const responseData = response.data
      if (responseData?.statusCode === 200) {
        setProviders(responseData.data || [])

        if (responseData.pagination) {
          setQueryState((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              currentPage: responseData.pagination.currentPage ?? prev.pagination.currentPage,
              pageSize: responseData.pagination.pageSize ?? prev.pagination.pageSize,
              totalPages: responseData.pagination.totalPages ?? 1,
              totalItems: responseData.pagination.totalItems ?? 0,
            },
          }))
        }
      }
    } catch (error) {
      console.error("Failed to fetch providers:", error)
    } finally {
      if (reqId === reqIdRef.current) setLoading(false)
    }
  }, [
    queryState.platform,
    queryState.searchValue,
    queryState.dateRange,
    queryState.pagination.currentPage,
    queryState.pagination.pageSize,
    queryState.refreshKey,
  ])

  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  /* ───────────────────── Helpers ───────────────────── */

  const platformOptions = useMemo(
    () => [
      { id: "ALL", value: "", name: "All Platforms" },
      { id: "Facebook", value: "Facebook", name: "Facebook" },
      { id: "Google", value: "Google", name: "Google" },
      { id: "TikTok", value: "TikTok", name: "TikTok" },
      { id: "Instagram", value: "Instagram", name: "Instagram" },
      { id: "Snapchat", value: "Snapchat", name: "Snapchat" },
      { id: "Other", value: "Other", name: "Other" },
    ],
    [],
  )

  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({ platform: "" })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }, [])

  const applyFilters = useCallback(() => {
    setQueryState((prev) => ({
      ...prev,
      platform: filters.platform,
      searchValue: searchQuery,
      dateRange,
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [filters.platform, searchQuery, dateRange])

  const resetAndRefresh = useCallback(() => {
    clearFilters()
    setQueryState((prev) => ({
      ...prev,
      platform: "",
      searchValue: "",
      dateRange: { start: "", end: "" },
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [clearFilters])

  const handleSearch = useCallback(() => {
    setQueryState((prev) => ({
      ...prev,
      platform: filters.platform,
      searchValue: searchQuery,
      dateRange,
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [filters.platform, searchQuery, dateRange])

  const handlePageChange = useCallback((newPage) => {
    setQueryState((prev) => {
      if (newPage < 1 || newPage > prev.pagination.totalPages) return prev
      return { ...prev, pagination: { ...prev.pagination, currentPage: newPage } }
    })
  }, [])

  const handlePageSizeChange = useCallback((e) => {
    const newSize = Number.parseInt(e.target.value, 10)
    setQueryState((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pageSize: newSize, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [])

  const getPageNumbers = useCallback((currentPage, totalPages) => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }, [])

  const formatDate = useCallback((raw) => {
    if (!raw) return "N/A"
    const dateString = String(raw).trim()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

    let d
    const m = dateString.match(dotDateRE)
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0,
      )
    } else {
      d = new Date(dateString)
    }
    if (isNaN(d.getTime())) return raw

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)

    if (!hasExplicitTime) return datePart

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })

    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"
    const hourNum = parseInt(hour, 10)
    const suffix = hourNum < 12 ? "AM" : "PM"
    return `${datePart}, ${hour}:${minute} ${suffix}`
  }, [])

  const getPlatformColor = useCallback((platform) => {
    const colorMap = {
      Facebook: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
      Google: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300",
      TikTok: "bg-neutral-900 text-white dark:bg-neutral-700/20 dark:text-neutral-100",
      Instagram: "bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300",
      Snapchat: "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
      Other: "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300",
    }
    return (
      colorMap[platform] ||
      "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300"
    )
  }, [])

  // helper: get fee % for a platform for display
  const feePct = useCallback((provider, platform) => {
    const f = (provider?.fees || []).find((x) => x.platform === platform)
    return typeof f?.feePercentage === "number" ? f.feePercentage : null
  }, [])

  /* ───────────────────── Actions ───────────────────── */

  const handleCreate = useCallback(() => {
    setSelectedProvider(null)
    setIsEditing(false)
    setShowProviderModal(true)
    closeDropdown()
  }, []) // closeDropdown declared below, but it’s stable (useCallback) so ok to keep as is

  const handleEditProvider = useCallback((provider) => {
    setSelectedProvider(provider)
    setIsEditing(true)
    setShowProviderModal(true)
    closeDropdown()
  }, []) // same note

  const handleDeleteProvider = useCallback((providerId) => {
    setDeleteId(providerId)
    setDeleteModal(true)
    closeDropdown()
  }, []) // same note

  const handleFormSuccess = useCallback(() => {
    setQueryState((prev) => ({ ...prev, refreshKey: prev.refreshKey + 1 }))
  }, [])

  const onCloseProviderModal = useCallback(() => {
    setShowProviderModal(false)
    setTimeout(() => {
      setSelectedProvider(null)
      setIsEditing(false)
      setQueryState((prev) => ({ ...prev, refreshKey: prev.refreshKey + 1 }))
    }, 250)
  }, [])

  /* ───────────────────── ✅ FIXED DROPDOWN (PORTAL) ───────────────────── */

  const [dropdownOpen, setDropdownOpen] = useState(null) // providerId
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const btnRefs = useRef(new Map())
  const menuRef = useRef(null)
  const rafRef = useRef(null)

  const closeDropdown = useCallback(() => setDropdownOpen(null), [])

  const openDropdown = useCallback((id) => {
    const btn = btnRefs.current.get(id)
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    setDropdownOpen(id)
    setDropdownPos(calcMenuPos(rect, 176 /* w-44 */, 220))
  }, [])

  const recomputeDropdown = useCallback(
    (id) => {
      const targetId = id ?? dropdownOpen
      if (!targetId) return
      const btn = btnRefs.current.get(targetId)
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const menuW = 176
      const menuH = menuRef.current?.offsetHeight || 220
      setDropdownPos(calcMenuPos(rect, menuW, menuH))
    },
    [dropdownOpen],
  )

  const toggleDropdown = useCallback(
    (id) => {
      setDropdownOpen((prev) => {
        const next = prev === id ? null : id
        if (next) openDropdown(id)
        return next
      })
    },
    [openDropdown],
  )

  // after mount, measure actual height and reposition (fixes last rows jumping)
  useLayoutEffect(() => {
    if (!dropdownOpen) return
    const t = requestAnimationFrame(() => recomputeDropdown(dropdownOpen))
    return () => cancelAnimationFrame(t)
  }, [dropdownOpen, recomputeDropdown])

  // outside click + esc + scroll/resize reposition
  useEffect(() => {
    if (!dropdownOpen) return

    const onMouseDown = (e) => {
      const menu = menuRef.current
      const btn = btnRefs.current.get(dropdownOpen)
      if (menu && menu.contains(e.target)) return
      if (btn && btn.contains(e.target)) return
      closeDropdown()
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDropdown()
    }

    const onMove = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => recomputeDropdown(dropdownOpen))
    }

    window.addEventListener("mousedown", onMouseDown)
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", onMove, true)
    window.addEventListener("resize", onMove)

    return () => {
      window.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", onMove, true)
      window.removeEventListener("resize", onMove)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [dropdownOpen, closeDropdown, recomputeDropdown])

  /* ───────────────────── Render ───────────────────── */

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="lg:p-7 p-3 max-w-full mx-auto"
    >
      {/* Top Bar */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Search */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search providers..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date */}
        <DateRangePicker
          onRangeChange={(range) => setDateRange(range)}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
            ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
            : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Apply filters */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">Apply Filters</span>
          </button>
        )}

        {/* Create */}
        <button
          className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 ml-auto"
          onClick={() => {
            setSelectedProvider(null)
            setIsEditing(false)
            setShowProviderModal(true)
            closeDropdown()
          }}
        >
          <PlusCircle size={16} />
          <span className="text-sm">Create Provider</span>
        </button>
      </div>

      {/* Active filters chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {searchQuery && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {(dateRange.start || dateRange.end) && (
            <Chip
              icon={<CalendarBlank size={12} />}
              label={`${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} - ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"
                }`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                handleSearch()
              }}
            />
          )}

          {filters.platform && (
            <Chip
              icon={<Users size={12} />}
              label={filters.platform}
              onClear={() => {
                handleFilterChange("platform", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* More filters panel */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={platformOptions}
              value={filters.platform}
              onChange={(value) => handleFilterChange("platform", value)}
              placeholder="Platform"
              icon={<Users size={16} />}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Providers Table + Pagination */}
      <div className="border border-neutral-150 dark:border-neutral-900 rounded-lg bg-white dark:bg-neutral-950">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              weight="bold"
              className="animate-[spin_0.8s_linear_infinite] text-neutral-600 dark:text-neutral-400"
            />
          </div>
        ) : providers.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-xl">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50/80 backdrop-blur-sm dark:bg-neutral-900/40">
                  <tr>
                    <Th>Created Time</Th>
                    <Th>Provider</Th>
                    <Th>Info</Th>
                    <Th>Type</Th>
                    <Th>Platforms &amp; Fees</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>

                <tbody className="bg-white dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-neutral-800 text-start">
                  {providers.map((provider, idx) => (
                    <motion.tr
                      key={provider._id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.16, delay: Math.min(idx * 0.02, 0.18) }}
                      className="hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 transition-colors"
                    >
                      {/* Date created */}
                      <Td>
                        <span className="text-sm text-neutral-900 dark:text-white">
                          {formatDate(provider.createdAt)}
                        </span>
                      </Td>

                      {/* Provider */}
                      <Td>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-neutral-100 dark:bg-neutral-900/60">
                            <Users size={16} className="text-neutral-600 dark:text-neutral-300" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-[400] text-neutral-900 dark:text-white truncate">
                              {provider.name}
                            </div>
                            {provider.notes && (
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                {provider.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </Td>
                      {/* Contact */}
                      <Td>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-neutral-900 dark:text-white whitespace-nowrap">
                            <EnvelopeSimple size={12} className="text-neutral-500 dark:text-neutral-400" />
                            {provider.email}
                          </div>
                          {provider.phone && (
                            <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                              <Phone size={12} />
                              {provider.phone}
                            </div>
                          )}
                        </div>
                      </Td>
                      {/* Type */}
                      <Td>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-[400] bg-green-600/10 text-green-600 dark:text-green-400">
                          <Tag size={12} className="mr-1.5" />
                          {Array.isArray(provider.type)
                            ? provider.type[0] || "-None-"
                            : provider?.type || "-None-"}
                        </span>
                      </Td>



                      {/* Platforms & Fees */}
                      <Td>
                        <div className="flex flex-wrap gap-2">
                          {provider.platforms?.map((platform) => {
                            const pct = feePct(provider, platform)
                            const meta = getPlatformMeta(platform)

                            return (
                              <span
                                key={platform}
                                title={meta.name}
                                className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${getPlatformColor(platform)}`}
                              >
                                {meta.icon}

                                {typeof pct === "number" && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/5">
                                    <Percent size={10} className="opacity-60" />
                                    {pct}%
                                  </span>
                                )}
                              </span>
                            )
                          })}

                        </div>
                      </Td>


                      {/* Actions */}
                      <Td className="text-sm font-[300]">
                        <button
                          ref={(node) => {
                            if (!node) btnRefs.current.delete(provider._id)
                            else btnRefs.current.set(provider._id, node)
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDropdown(provider._id)
                          }}
                          className="p-1.5 rounded-xl text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all"
                          aria-label="Actions"
                          type="button"
                        >
                          <DotsThreeVertical size={16} weight="bold" />
                        </button>
                      </Td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 bg-neutral-50/60 dark:bg-neutral-900/40 rounded-b-xl">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-[13px] text-neutral-600 dark:text-neutral-400">
                  Showing {providers.length} of {pagination.totalItems} providers
                </span>

                <div className="ml-4 flex items-center gap-2">
                  <span className="text-[12px] text-neutral-500 dark:text-neutral-400">Rows</span>
                  <div className="relative">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 pl-3 pr-8 text-[12px] border border-neutral-200/80 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-neutral-100 shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-300 dark:focus:ring-neutral-600 appearance-none cursor-pointer"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                    <CaretDown
                      size={14}
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  aria="First page"
                >
                  <CaretDoubleLeft size={14} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={14} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-7 h-7 flex items-center justify-center rounded-xl text-[13px] ${pagination.currentPage === page
                          ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={14} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={14} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">
              No Providers Found
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {filtersActive
                ? "No providers match your search criteria. Try adjusting your filters or search term."
                : "There are no providers in the system yet. Create your first provider to get started."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>

              <button
                onClick={() => {
                  setSelectedProvider(null)
                  setIsEditing(false)
                  setShowProviderModal(true)
                  closeDropdown()
                }}
                className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <PlusCircle size={16} />
                <span>Create Provider</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ✅ One dropdown portal (prevents “jumping above” near bottom rows) */}
      {createPortal(
        <AnimatePresence>
          {dropdownOpen ? (
            <motion.div
              ref={menuRef}
              className="fixed w-44 bg-white dark:bg-neutral-900/95 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 z-[9999]"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.14 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  const p = providers.find((x) => x._id === dropdownOpen)
                  if (p) handleEditProvider(p)
                }}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                <PencilSimple size={14} className="mr-2" />
                Edit
              </button>

              <button
                onClick={() => {
                  const p = providers.find((x) => x._id === dropdownOpen)
                  if (p) openDetails(p)
                  closeDropdown()
                }}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                <ListBullets size={14} className="mr-2" />
                View
              </button>

              <button
                onClick={() => handleDeleteProvider(dropdownOpen)}
                className="flex items-center w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <TrashSimple size={14} className="mr-2" />
                Delete
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="providers"
        setDone={() => setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))}
        setLoding={setLoading}
        done={queryState.refreshKey}
      />

      {/* Provider Modal */}
      <ProviderModal
        isOpen={showProviderModal}
        onClose={onCloseProviderModal}
        onSuccess={handleFormSuccess}
        providerId={selectedProvider?._id}
        isEditing={isEditing}
      />

      {/* Provider Details */}
      <ProviderDetailsModal
        isOpen={showDetails}
        onClose={closeDetails}
        providerId={detailsId}
        onEdit={(id) => {
          closeDetails()
          const p = providers.find((x) => x._id === id)
          if (p) handleEditProvider(p)
        }}
      />
    </motion.div>
  )
}

/* ───────────────────── Small UI helpers ───────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-5 text-left text-[12px] font-[400] text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-200 ${className}`}>
      {children}
    </td>
  )
}

function Chip({ label, onClear, icon }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1 rounded-xl ${disabled
        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}
